import type { ReactNode } from 'react';
import { useContext } from 'react';
import {
    LogInteractionDialogContext,
    type LogInteractionDialogContextValue,
} from './log-interaction-dialog-context.ts';

type LogInteractionDialogProviderProps = LogInteractionDialogContextValue & {
    children: ReactNode;
};

/**
 * Shares what every section of the log interaction dialog needs — the analytics session,
 * the customer being logged against, and the staged tasks — so none of it has to be
 * threaded down through the sections. The form itself stays with the component that owns
 * `useForm`; only the derived values travel.
 */
export function LogInteractionDialogProvider({
    session,
    customerId,
    customerName,
    stagedTasks,
    addTasks,
    children,
}: LogInteractionDialogProviderProps): ReactNode {
    return (
        <LogInteractionDialogContext.Provider
            value={{ session, customerId, customerName, stagedTasks, addTasks }}
        >
            {children}
        </LogInteractionDialogContext.Provider>
    );
}

/** Everything the open dialog shares with its sections. */
export function useLogInteractionDialog(): LogInteractionDialogContextValue {
    const value = useContext(LogInteractionDialogContext);
    if (!value) {
        throw new Error('useLogInteractionDialog must be used inside a LogInteractionDialogProvider');
    }
    return value;
}
